import React, { useState, useEffect, useRef } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useSupplier } from "../../../apis/supplier/SupplierProvider";
import { useClient } from "../../../apis/client/ClientProvider";
import { getUiInterface, getPageName, getWordConnector, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import Supplier from "../../../types/supplier";
import { FaChevronDown } from "react-icons/fa";

const SupplierCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postSupplier } = useSupplier();
    const { clients, fetchClient } = useClient();
    const navigate = useNavigate();
    const selectRef = useRef<(HTMLSelectElement | null)>(null);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const [supplier, setSupplier] = useState<Supplier>({
        id: 0,
        name: "",
        cnpj: "",
        location: {
            id: 0,
            latitude: "",
            longitude: "",
            address: {
                id: 0,
                street: "",
                number: "",
                complement: "",
                city: "",
                state: "",
                country: "",
                zipCode: "",
            },
        },
        client: {
            id: 0,
            name: '',
            cnpj: '',
        }
    } as Supplier);

    const navItems: NavBarItem[] = [
        { label: t('supplier.labels.home'), route: "/", iconName: "house-door" },
        { label: t('supplier.labels.list'), route: "/Supplier", iconName: "table" },
    ];

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;

        setSupplier((prevSupplier) => {
            const keys = name.split(".");
            const updatedSupplier = { ...prevSupplier };
            let current: any = updatedSupplier;

            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                current[key] = { ...current[key] };
                current = current[key];
            }

            current[keys[keys.length - 1]] = value;
            
            return updatedSupplier;
        });
    };

    const handleClientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setSupplier((prevSupplier) => {
            return {
                ...prevSupplier,
                client: {
                    id: Number(value)
                }
            }
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];

        if (!supplier.name) nullRequiredFields.push("Name");
        if (!supplier.client?.id) nullRequiredFields.push("Client");

        if (nullRequiredFields.length > 0) {
            setInvalidFields(new Set(nullRequiredFields));
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        } else {
            setInvalidFields(new Set());
        }

        try {
            const response = await postSupplier(supplier);

            if (response?.data?.id) {
                const successMessage = getSuccessMessage("createSupplier", { id: response.data.id });
                navigate(`/Supplier`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("createSupplierError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    useEffect(() => {
        fetchClient(null);
    }, []);

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createSupplier")}</h2>
            <Form 
                onSubmit={handleSubmit}
                style={{paddingBottom: "32px"}}
            >
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('supplier.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={supplier.name}
                                isInvalid={invalidFields.has("Name")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    {/* <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>CNPJ</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={supplier.cnpj}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col> */}

                    <Col md={6} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('supplier.fields.client')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    as="select"
                                    name="client"
                                    value={supplier.client?.id}
                                    isInvalid={invalidFields.has("Client")}
                                    onChange={handleClientChange}
                                    ref={(el: HTMLSelectElement | null) => {
                                        if (el) selectRef.current = el;
                                    }}
                                    style={{ 
                                        backgroundImage: 'none', 
                                        appearance: "none", 
                                        paddingRight: "30px",
                                        cursor: "pointer" 
                                    }}
                                >
                                    <option value="">{t('supplier.labels.selectClient')}</option>
                                    {
                                        (clients || []).map((client) => (
                                            <option
                                                key={client.id}
                                                value={client.id}>
                                                {`Id: ${client.id} | Nome: ${client.name}`}
                                            </option>
                                        ))
                                    }
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "20px",
                                        pointerEvents: "auto",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => selectRef?.current?.focus()}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                {/* <Row>
                    <h5>Location</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>Lat</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={supplier.location?.latitude}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>Lng</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={supplier.location?.longitude}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>Address</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>Street</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={supplier.location?.address?.street}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={supplier.location?.address?.number}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>Complement</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={supplier.location?.address?.complement}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>City</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={supplier.location?.address?.city}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>State</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={supplier.location?.address?.state}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>Country</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={supplier.location?.address?.country}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>Zip Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={supplier.location?.address?.zipCode}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row> */}

                <Button variant="primary" onClick={handleSubmit}>
                    {getUiInterface("create")}
                </Button>
            </Form>
        </Container>
    );
};

export default SupplierCreateView;